// Angular wrapper for DateImplService - delegates all calls to the implementation
// This allows the same date logic to be used in both Angular apps and Firebase Cloud Functions

import { Injectable } from '@angular/core';
import type { Duration } from 'date-fns';
import type { TTimestamp } from '@shared/models';
import { DateImplService } from './dateImpl.service';

@Injectable({
    'providedIn': 'root'
})
export class DateService {
    public static localTimezone: string;

    // No longer needed - DateImplService handles initialization
    public createTimestamp(seconds = 0, nanoseconds = 0): TTimestamp {
        return DateImplService.createTimestamp(seconds, nanoseconds);
    }

    public now(): TTimestamp {
        return DateImplService.now();
    }

    public getMinimumDate(): TTimestamp {
        return DateImplService.tsMinimumDate;
    }

    public getMaximumDate(): TTimestamp {
        return DateImplService.tsMaximumDate;
    }

    public isMinimumDate(ts: TTimestamp): boolean {
        return DateImplService.isMinimumDate(ts);
    }

    public toTimestamp(dt: Date | number | string): TTimestamp {
        return DateImplService.toTimestamp(dt);
    }

    public toDate(timestamp: TTimestamp): Date {
        return DateImplService.toDate(timestamp);
    }

    public parse(datetime: string, format: string, setToMidnight = true): TTimestamp {
        return DateImplService.parse(datetime, format, setToMidnight);
    }

    public timestampToUTC(timestamp: TTimestamp): string {
        return DateImplService.timestampToUTC(timestamp);
    }

    public UTCToTimestamp(utcDatetime: string): TTimestamp {
        return DateImplService.UTCToTimestamp(utcDatetime);
    }

    public isBeforeToday(timestamp: TTimestamp): boolean {
        return DateImplService.isBeforeToday(timestamp);
    }

    public isBeforeNow(timestamp: TTimestamp): boolean {
        return DateImplService.isBeforeNow(timestamp);
    }

    public isAfterNow(timestamp: TTimestamp): boolean {
        return DateImplService.isAfterNow(timestamp);
    }

    public formatTimestamp(timestamp: TTimestamp | string, pattern?: string): string {
        return DateImplService.formatTimestamp(timestamp, pattern);
    }

    public addToNow(duration: number, unit: string, setToMidnight = true): TTimestamp {
        return DateImplService.addToNow(duration, unit, setToMidnight);
    }

    public add(timestamp: TTimestamp, duration: number, unit: string, setToMidnight = true): TTimestamp {
        return DateImplService.add(timestamp, duration, unit, setToMidnight);
    }

    public setTimestampToStartOfDay(timestamp: TTimestamp): TTimestamp {
        return DateImplService.setTimestampToStartOfDay(timestamp);
    }

    public setTimestampToEndOfDay(timestamp: TTimestamp): TTimestamp {
        return DateImplService.setTimestampToEndOfDay(timestamp);
    }

    public setHoursMinutesSeconds(timestamp: TTimestamp, numHours: number, numMinutes: number,
        numSeconds: number): TTimestamp {
        return DateImplService.setHoursMinutesSeconds(timestamp, numHours, numMinutes, numSeconds);
    }

    public adjustTimeBoundary(timestamp: TTimestamp, slot: 'start' | 'end'): TTimestamp {
        return DateImplService.adjustTimeBoundary(timestamp, slot);
    }

    public get currentDate(): string {
        return DateImplService.currentDate;
    }

    public get currentTime(): string {
        return DateImplService.currentTime;
    }

    public getDateValue(): number {
        return DateImplService.getDateValue();
    }

    public formatTimeAndDuration(start: TTimestamp, end: TTimestamp): string {
        return DateImplService.formatTimeAndDuration(start, end);
    }

    public computeDuration(start: TTimestamp, end: TTimestamp): Duration {
        return DateImplService.computeDuration(start, end);
    }

    public compareDuration(duration1: Duration, duration2: Duration): number {
        return DateImplService.compareDuration(duration1, duration2);
    }

    public formatDuration(start: TTimestamp, end: TTimestamp): string {
        return DateImplService.formatDuration(start, end);
    }

    public datesEqual(ts1: TTimestamp, ts2: TTimestamp): boolean {
        return DateImplService.datesEqual(ts1, ts2);
    }

    public getReadableDate(ts1: TTimestamp, format?: string): string {
        return DateImplService.getReadableDate(ts1, format);
    }

    public getTimezone(): string {
        return DateImplService.getTimezone();
    }

    public setLocalTimezone(timezone: string): void {
        DateImplService.setLocalTimezone(timezone);
    }

    public convertServerTimestampToToLocalTime(serverTimestamp: TTimestamp, localTimezone: string): void {
        DateImplService.convertServerTimestampToToLocalTime(serverTimestamp, localTimezone);
    }

    public isTimestampToday(ts: TTimestamp): boolean {
        return DateImplService.isTimestampToday(ts);
    }
}
